import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  CrosshairMode,
  ISeriesApi,
  MouseEventParams,
  Time
} from 'lightweight-charts';
import { useCallback, useEffect, useRef } from 'react';
import { handleTooltip } from './utils/tooltip.utils';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import { useMaSeries } from './use-ma-series';
import { useOrderBlocks } from './order-blocks.hook';
import { useZigZagSeries } from './zig-zag.hook';
import { useMarkers } from './markers.hook';
import { useMasterPattern } from './master-pattern.hook';
import { useMarketStructure } from './market-structure.hook';
import { useVwapSeries } from './use-vwap-series';
import { useLiquidationSeries } from './use-liquidation-series';
import {
  CandlestickUpdate,
  CandlestickUpdatePoint,
  mergeCandlestickPoints
} from '../services/candlestick-update.service';
import { useCustomMarkers } from './custom-markers.hook';

const countDecimals = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  const valueString = value.toString();
  if (valueString.includes('e-')) {
    const [base, exp] = valueString.split('e-');
    const baseDecimals = base.split('.')[1]?.length ?? 0;
    return baseDecimals + parseInt(exp, 10);
  }

  return valueString.split('.')[1]?.length ?? 0;
};

const resolvePricePrecision = (candlesticks: any[]) => {
  let maxDecimals = 0;
  candlesticks.forEach((candle) => {
    maxDecimals = Math.max(
      maxDecimals,
      countDecimals(candle.open),
      countDecimals(candle.high),
      countDecimals(candle.low),
      countDecimals(candle.close)
    );
  });

  return Math.min(maxDecimals, 10);
};

const applyPricePrecision = (
  precision: number,
  ...series: (ISeriesApi<any> | null)[]
) => {
  if (precision <= 0) return;
  const priceFormat = {
    type: 'price' as const,
    precision,
    minMove: Math.pow(10, -precision)
  };

  series.forEach((s) => s?.applyOptions({ priceFormat }));
};

const toAreaPoint = (datapoint: CandlestickUpdatePoint) => ({
  time: datapoint.time,
  value: (datapoint.close + datapoint.open) / 2
});

export const useCandlestickChart = () => {
  const {
    chartContainerRef,
    chartRef,
    candlestickSeriesRef,
    overlayIndicatorSeriesRefs,
    indicatorSeriesRefs,
    indicatorChartRefs,
    tooltipRef,
    initialLoad,
    chartInitialized,
    setContext,
    data: { indicatorConfigs, execution }
  } = useChartContext();
  const {
    timeframeIndex,
    formState,
    candlestickUpdate$,
    getCandlestickSnapshots
  } = useChartStreamContext();
  const isLiveTrading = formState.marketConfig?.tradingMode === 'LIVE';

  const areaSeriesRef = useRef<ISeriesApi<any> | null>(null);
  const candlesticksRef = useRef<CandlestickUpdatePoint[]>([]);
  const executionRef = useRef(execution);

  useEffect(() => {
    executionRef.current = execution;
  }, [execution]);

  // Overlay indicators driven by streaming updates.
  useMaSeries();
  useVwapSeries();
  useZigZagSeries();
  useMasterPattern();
  useOrderBlocks();
  useMarketStructure();
  useLiquidationSeries();

  useMarkers();
  const { initializeOrUpdate: initializeOrUpdateMarkers } = useCustomMarkers();

  const createCandlestickChart = useCallback(() => {
    if (!chartContainerRef.current || chartInitialized) return;

    chartRef.current = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: { background: { color: 'transparent' }, textColor: '#DDD' },
      grid: {
        vertLines: { color: '#444', visible: false },
        horzLines: { color: '#444', visible: false }
      },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: {
        visible: !isLiveTrading && !indicatorConfigs.subplot.length
      }
    });

    areaSeriesRef.current = chartRef.current?.addSeries(AreaSeries, {
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      lineColor: 'transparent',
      topColor: 'rgba(56, 33, 110,0.6)',
      bottomColor: 'rgba(56, 33, 110, 0.1)'
    });

    candlestickSeriesRef.current = chartRef.current.addSeries(
      CandlestickSeries,
      {
        wickUpColor: 'rgb(54, 116, 217)',
        upColor: 'rgb(54, 116, 217)',
        wickDownColor: 'rgb(225, 50, 85)',
        downColor: 'rgb(225, 50, 85)',
        borderVisible: false,
        priceLineVisible: false
      }
    );
    setContext({ chartInitialized: true });
    // Don't add any dependencies here, otherwise the chart will be recreated, which will cause problems
    // Use a separate hook for additional logic
  }, [indicatorConfigs.subplot.length, chartInitialized, isLiveTrading]);

  useEffect(() => {
    if (!chartInitialized) return;
    const handleCrosshairMove = (param: MouseEventParams<Time>) => {
      handleTooltip(
        param,
        tooltipRef,
        chartRef,
        candlestickSeriesRef,
        candlesticksRef.current,
        executionRef.current
      );
    };

    chartRef.current?.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      chartRef.current?.unsubscribeCrosshairMove(handleCrosshairMove);
    };
  }, [chartInitialized]);

  const applyCandlestickUpdate = useCallback(
    (update: CandlestickUpdate) => {
      const candlestickSeries = candlestickSeriesRef.current;
      const areaSeries = areaSeriesRef.current;
      if (!candlestickSeries || !areaSeries) return;

      if (update.updateType === 'snapshot') {
        candlesticksRef.current = update.data;

        const precision = resolvePricePrecision(update.data);
        applyPricePrecision(precision, candlestickSeries, areaSeries);

        candlestickSeries.setData(update.data);
        areaSeries.setData(update.data.map(toAreaPoint));

        if (initialLoad && update.data.length) {
          chartRef.current?.timeScale().fitContent();
          setContext({ initialLoad: false });
        }
        return;
      }

      update.data.forEach((point) => {
        candlestickSeries.update(point);
        areaSeries.update(toAreaPoint(point));
      });

      candlesticksRef.current = mergeCandlestickPoints(
        candlesticksRef.current,
        update.data
      );

      const precision = resolvePricePrecision(candlesticksRef.current);
      applyPricePrecision(precision, candlestickSeries, areaSeries);
    },
    [initialLoad, setContext]
  );

  useEffect(() => {
    if (!chartInitialized) return;
    candlesticksRef.current = [];
    candlestickSeriesRef.current?.setData([]);
    areaSeriesRef.current?.setData([]);
  }, [chartInitialized, timeframeIndex]);

  useEffect(() => {
    if (!chartInitialized) return;

    getCandlestickSnapshots().forEach(applyCandlestickUpdate);

    const subscription = candlestickUpdate$.subscribe((update) => {
      applyCandlestickUpdate(update);
    });

    return () => subscription.unsubscribe();
  }, [
    applyCandlestickUpdate,
    candlestickUpdate$,
    chartInitialized,
    getCandlestickSnapshots,
    timeframeIndex
  ]);

  const initializePrimitives = useCallback(() => {
    initializeOrUpdateMarkers();
  }, [initializeOrUpdateMarkers]);

  useEffect(() => {
    if (!chartInitialized) return;

    initializePrimitives();
  }, [chartInitialized, initializePrimitives, indicatorConfigs.overlay]);

  useEffect(() => {
    return () => {
      chartRef.current?.remove();

      chartRef.current = null;
      candlestickSeriesRef.current = null;
      areaSeriesRef.current = null;
      candlesticksRef.current = [];
      overlayIndicatorSeriesRefs.current = {};
      indicatorSeriesRefs.current = {};
      indicatorChartRefs.current = {};
    };
  }, [
    chartRef,
    candlestickSeriesRef,
    indicatorChartRefs,
    indicatorSeriesRefs,
    overlayIndicatorSeriesRefs
  ]);

  return {
    createCandlestickChart
  };
};
